import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { alpha, useTheme } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import SimpleDrawer from '../../../shared/simple-drawer';
import StatusLabel from '../../../shared/status-label';
import { getStatCardTokens } from '../../../theme/statCardTheme';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import EmptyStateMessage from '../../../shared/empty-state-message';
import AvatarChip from '../../../shared/avatar-chip';
import { getBadgeColorsForChoice } from '../../../shared/google-color';
import {
  getDriveLabelLocksQuery,
  getDriveLabelPermissionsQuery,
} from '../../../api/users-api';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import Filter1Icon from '@mui/icons-material/Filter1';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GmailIcon from '../../../assets/google-apps/gmail.png';
import DriveIcon from '../../../assets/subscriptions/drive.png';
import FieldTypeIcon from './create-label/field-type-icon';
import { detectFieldType } from './create-label/label-draft-mapper';
import { FIELD_TYPE_CONFIG } from './create-label/label-schema';
import { VAULT_RETENTION_URL } from './create-label/step-rules';
import WarningCallout from './create-label/warning-callout';
import LabelActionsMenu from './label-actions-menu';
import type { LabelActionHandlers } from './use-label-actions';
import { colors } from '../../../theme/colors';
import type { LabelFieldType } from '../../../api/drive-labels-api';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const GOOGLE_WORKSPACE_APP_ICONS: Record<string, string> = {
  GMAIL: GmailIcon,
  DRIVE: DriveIcon,
};

const COPY_MODE_LABELS: Record<string, string> = {
  COPY_APPLIABLE: 'Copy label if user can apply',
  ALWAYS_COPY: 'Always copy label',
  DO_NOT_COPY: "Don't copy label",
};

const FIELDS_TILE_LIGHT_BACKGROUND = '#D6F0EF';
const FIELDS_TILE_LIGHT_TEXT = '#28716F';

function FieldsCountTile({ value }: { value: number }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const darkTokens = getStatCardTokens(theme, 'teal');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flexGrow: 0,
        width: 'fit-content',
        minWidth: 380,
        pt: 1,
        pr: 1,
        pb: 1,
        pl: 2,
        borderRadius: '4px',
        bgcolor: isDarkMode
          ? darkTokens.background
          : FIELDS_TILE_LIGHT_BACKGROUND,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: isDarkMode ? darkTokens.textColor : FIELDS_TILE_LIGHT_TEXT,
        }}
      >
        Fields
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 400,
          color: isDarkMode ? darkTokens.textColor : FIELDS_TILE_LIGHT_TEXT,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function getLabelStatus(
  state: string | null | undefined
): 'PUBLISHED' | 'DRAFT' | 'DISABLED' | 'UNKNOWN' {
  const normalized = String(state ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'published') return 'PUBLISHED';
  if (normalized === 'unpublished_draft') return 'DRAFT';
  if (normalized === 'disabled') return 'DISABLED';
  return 'UNKNOWN';
}

interface FieldCardProps {
  icon: React.ReactNode;
  iconBg?: string;
  title: string;
  description: string;
  trailingText: string;
  expanded: boolean;
  onToggleExpand: () => void;
  disabled?: boolean;
  /** Shows a lock next to the trailing text when a Google rule locks this field. */
  locked?: boolean;
  children?: React.ReactNode;
}

function FieldCard({
  icon,
  iconBg,
  title,
  description,
  trailingText,
  expanded,
  onToggleExpand,
  disabled = false,
  locked = false,
  children,
}: FieldCardProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const canExpand = Boolean(children);
  const isOpen = expanded && canExpand;
  // Design: outline/lightest around a closed card, outline/lighter around an open one.
  const borderColor = isOpen
    ? isDarkMode
      ? colors.neutral[600]
      : colors.neutral[300]
    : isDarkMode
    ? theme.palette.divider
    : colors.neutral[100];

  const header = (
    <Box
      onClick={canExpand ? onToggleExpand : undefined}
      sx={{
        display: 'flex',
        height: 72,
        px: 2,
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderBottom: isOpen ? `1px solid ${borderColor}` : 'none',
        cursor: canExpand ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ minWidth: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            width: 44,
            height: 44,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            borderRadius: '4px',
            bgcolor: iconBg,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            noWrap
            sx={{ lineHeight: '20px' }}
          >
            {description}
          </Typography>
          <Typography sx={{ fontWeight: 500 }} noWrap>
            {title}
          </Typography>
        </Box>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexShrink: 0, pl: 1 }}
      >
        {trailingText && (
          <Typography variant="caption" color="text.secondary">
            {trailingText}
          </Typography>
        )}
        {locked && (
          <Tooltip title="Locked by a Google rule" arrow>
            <LockOutlinedIcon
              aria-label="Field locked"
              sx={{ fontSize: 16, color: 'text.secondary' }}
            />
          </Tooltip>
        )}
        {canExpand && (
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse field' : 'Expand field'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: '0.2s',
              }}
            />
          </IconButton>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        mb: 1,
        border: `1px solid ${borderColor}`,
        borderRadius: '4px',
        overflow: 'hidden',
        bgcolor: isDarkMode ? 'transparent' : 'background.paper',
      }}
    >
      {disabled ? (
        <Tooltip title="Disabled" arrow placement="top-start">
          {header}
        </Tooltip>
      ) : (
        header
      )}
      {isOpen && <Box sx={{ p: 2 }}>{children}</Box>}
    </Box>
  );
}

/** Lists show this many items before "show more", as in the design. */
const LIST_VISIBLE_COUNT = 3;

function ShowMoreToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <ButtonBase
      onClick={onToggle}
      sx={{
        alignSelf: 'center',
        gap: 0.5,
        pb: 1,
        color: 'text.secondary',
        borderRadius: 1,
      }}
    >
      {expanded ? (
        <ExpandLessIcon sx={{ fontSize: 16 }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 16 }} />
      )}
      <Typography sx={{ fontSize: 10, fontWeight: 500, lineHeight: '14px' }}>
        {expanded ? 'show less' : 'show more'}
      </Typography>
    </ButtonBase>
  );
}

interface DetailListBoxProps {
  title: string;
  items: { title: string; description: string }[];
  emptyMessage: string;
}

function DetailListBox({ title, items, emptyMessage }: DetailListBoxProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const canCollapse = items.length > LIST_VISIBLE_COUNT;
  const visibleItems =
    expanded || !canCollapse ? items : items.slice(0, LIST_VISIBLE_COUNT);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', mb: 1, fontSize: 14 }}
      >
        {title} ({items.length})
      </Typography>
      <Box
        sx={{
          p: 2,
          height: '100%',
          bgcolor: theme.palette.mode === 'dark' ? 'transparent' : 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {visibleItems.map((item, index) => (
              // eslint-disable-next-line react-x/no-array-index-key -- titles are not unique
              <Stack key={`${item.title}-${index}`}>
                <Typography variant="body2">{item.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.description}
                </Typography>
              </Stack>
            ))}
            {canCollapse && (
              <ShowMoreToggle
                expanded={expanded}
                onToggle={() => setExpanded((prev) => !prev)}
              />
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

type LabelPermission =
  drivelabels_v2.Schema$GoogleAppsDriveLabelsV2LabelPermission;

const ROLE_LABELS: Record<string, string> = {
  READER: 'Can view files with this label',
  APPLIER: 'Can apply this label',
  ORGANIZER: 'Can manage & edit',
  EDITOR: 'Can edit this label',
};

function describePermission(permission: LabelPermission): {
  principal: string;
  role: string;
} {
  const principal = permission.audience
    ? 'Entire organization'
    : permission.email || permission.group || permission.person || 'Unknown';
  const role = permission.role
    ? ROLE_LABELS[permission.role] || permission.role
    : 'N/A';
  return { principal, role };
}

type LabelField = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Field;

interface LabelDetailsDrawerProps {
  selectedLabel: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label & {
    rankOrder: number;
    hasBadge: boolean;
  };
  onClose: () => void;
  /** Publish, Clone, Delete, Disable and Enable for this label. Without it the drawer has no actions menu. */
  labelActions?: LabelActionHandlers;
  /** Opens Edit Label from the actions menu, as in the grid row's menu. */
  onEdit?: (label: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label) => void;
}

function formatDate(iso?: string | null): string {
  if (!iso) return 'N/A';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

const FIELD_ICON_BG_LIGHT = '#E8F2FD'; // Background/Surface BG/blue
const FIELD_ICON_BG_DARK = alpha(blue[300], 0.16);
const FIELD_ICON_COLOR_LIGHT = blue[700]; // Background/Icons/blue
const FIELD_ICON_COLOR_DARK = blue[300];

const LOCKED_INDICATOR_COLOR = colors.neutral[700]; // Background/Text/body in the design

/** Google Admin console page for one label; the label id carries no customer or domain. */
const ADMIN_CONSOLE_LABEL_URL = 'https://admin.google.com/ac/dc/labels/';

/**
 * Where a field shows up: the label's apps, minus Gmail for field types Gmail doesn't show.
 */
function getFieldAppsText(
  type: LabelFieldType,
  driveEnabled: boolean,
  gmailEnabled: boolean
): string | null {
  const inGmail = gmailEnabled && FIELD_TYPE_CONFIG[type].gmailSupported;
  if (driveEnabled && inGmail) return 'Drive & Gmail';
  if (driveEnabled) return 'Drive only';
  if (inGmail) return 'Gmail only';
  return null;
}

/** The detail on the right of a field row: option count, date format or multiple people. */
function getFieldSummary(field: LabelField, type: LabelFieldType): string {
  if (FIELD_TYPE_CONFIG[type].hasOptions) {
    const count = field.selectionOptions?.choices?.length ?? 0;
    return `${count} ${count === 1 ? 'option' : 'options'}`;
  }
  if (type === 'DATE') {
    const format = field.dateOptions?.dateFormatType;
    if (format === 'LONG_DATE') return 'Format: Long';
    if (format === 'SHORT_DATE') return 'Format: Short';
    return '';
  }
  if (type === 'PERSON' && field.userOptions?.listOptions) {
    return 'Select multiple: allowed';
  }
  return '';
}

function getFieldTypeMeta(
  field: LabelField,
  isDarkMode: boolean
): {
  type: LabelFieldType;
  label: string;
  icon: React.ReactNode;
  iconBg: string;
} {
  const iconSx = {
    color: isDarkMode ? FIELD_ICON_COLOR_DARK : FIELD_ICON_COLOR_LIGHT,
  };

  // Same icons and type names as Create Label (from the design), so a field looks the same in both.
  const type = detectFieldType(field);
  const label = FIELD_TYPE_CONFIG[type].label;

  return {
    type,
    label,
    icon: <FieldTypeIcon type={type} sx={iconSx} />,
    iconBg: isDarkMode ? FIELD_ICON_BG_DARK : FIELD_ICON_BG_LIGHT,
  };
}

export default function LabelDetailsDrawer({
  selectedLabel,
  onClose,
  labelActions,
  onEdit,
}: LabelDetailsDrawerProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [expandedFields, setExpandedFields] = useState<Set<string>>(
    () => new Set()
  );

  const toggleField = (id: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const [showAllFields, setShowAllFields] = useState(false);

  const fields = selectedLabel.fields ?? [];
  const visibleFields =
    showAllFields || fields.length <= LIST_VISIBLE_COUNT
      ? fields
      : fields.slice(0, LIST_VISIBLE_COUNT);
  const labelStatus = getLabelStatus(selectedLabel.lifecycle?.state);

  const { data: permissions, isLoading: permissionsLoading } = useQuery(
    getDriveLabelPermissionsQuery(selectedLabel.id ?? '')
  );

  const { data: locks } = useQuery(
    getDriveLabelLocksQuery(selectedLabel.id ?? '')
  );
  const activeLocks = (locks ?? []).filter((lock) => lock.state === 'ACTIVE');
  const isLocked = activeLocks.length > 0;
  const isFieldLocked = (fieldId?: string | null) =>
    activeLocks.some((lock) => lock.fieldId === fieldId && !lock.choiceId);
  const isChoiceLocked = (fieldId?: string | null, choiceId?: string | null) =>
    activeLocks.some(
      (lock) => lock.fieldId === fieldId && lock.choiceId === choiceId
    );

  const enabledAppKeys = (
    selectedLabel.enabledAppSettings?.enabledApps ?? []
  ).map((enabledApp) => (enabledApp.app ?? '').toUpperCase());
  const driveEnabled = enabledAppKeys.includes('DRIVE');
  const gmailEnabled = enabledAppKeys.includes('GMAIL');

  const appsDisplay = useMemo(() => {
    const enabledApps = selectedLabel.enabledAppSettings?.enabledApps ?? [];
    const copyMode = selectedLabel.appliedLabelPolicy?.copyMode;
    const copyModeText = copyMode ? COPY_MODE_LABELS[copyMode] : undefined;

    if (enabledApps.length === 0) {
      return 'None';
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap">
          {enabledApps.map((enabledApp, index) => {
            const appKey = enabledApp.app ?? '';
            const iconSrc = GOOGLE_WORKSPACE_APP_ICONS[appKey.toUpperCase()];
            return (
              <Stack
                // eslint-disable-next-line react-x/no-array-index-key -- app names are not unique
                key={`${appKey}-${index}`}
                direction="row"
                alignItems="center"
                spacing={0.5}
              >
                {iconSrc && (
                  <Box
                    component="img"
                    src={iconSrc}
                    alt={appKey}
                    sx={{ width: 18, height: 18 }}
                  />
                )}
                <Typography variant="body2">
                  {appKey ? capitalize(appKey) : 'Unknown'}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        {copyModeText && (
          <Typography variant="body2" color="text.secondary">
            · {copyModeText}
          </Typography>
        )}
      </Stack>
    );
  }, [selectedLabel.enabledAppSettings, selectedLabel.appliedLabelPolicy]);

  const basicInfoData: Record<string, React.ReactNode> = useMemo(
    () => ({
      'Created By': selectedLabel.creator?.person ? (
        <AvatarChip
          email={selectedLabel.creator.person}
          type="User"
          label={selectedLabel.creator.person}
        />
      ) : (
        'N/A'
      ),
      'Last Modified By': selectedLabel.revisionCreator?.person ? (
        <AvatarChip
          email={selectedLabel.revisionCreator.person}
          type="User"
          label={selectedLabel.revisionCreator.person}
        />
      ) : (
        'N/A'
      ),
      Rank: selectedLabel.rankOrder,
      Type: selectedLabel.hasBadge ? 'Badged Label' : 'Standard Label',
      Apps: appsDisplay,
    }),
    [selectedLabel, appsDisplay]
  );

  return (
    <SimpleDrawer
      title="Label Details"
      onClose={onClose}
      headerActions={
        labelActions && (
          <LabelActionsMenu
            label={selectedLabel}
            hasActiveLock={isLocked}
            onPublish={labelActions.requestPublish}
            onClone={labelActions.cloneLabel}
            onDelete={(label) => labelActions.requestDelete([label])}
            onDisable={(label) => labelActions.requestDisable([label])}
            onEnable={(label) => labelActions.requestEnable([label])}
            isDeleting={labelActions.isDeleting}
            isPublishing={labelActions.isPublishing}
            onEdit={onEdit}
          />
        )
      }
    >
      {isLocked && (
        <Box sx={{ mb: 2 }}>
          <WarningCallout
            bordered
            padding={2}
            icon={<LockOutlinedIcon />}
            footer={
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Link
                  href={`${ADMIN_CONSOLE_LABEL_URL}${selectedLabel.id ?? ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  color="inherit"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Open Admin Console
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </Link>
                <Link
                  href={VAULT_RETENTION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  color="inherit"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Open Vault
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </Link>
              </Stack>
            }
          >
            <Typography variant="body2">
              This label is locked because it's used in a Google rule. You can
              view and manage this rule in Google Admin or Vault.
            </Typography>
          </WarningCallout>
        </Box>
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {selectedLabel.properties?.title || 'N/A'}
          </Typography>
          {selectedLabel.hasBadge && (
            <Tooltip title="This is a badged label" arrow>
              <LocalOfferOutlinedIcon
                sx={{ width: 20, height: 20 }}
                color="action"
              />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <StatusLabel status={labelStatus} />
          {isLocked && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <LockOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: isDarkMode ? 'text.secondary' : LOCKED_INDICATOR_COLOR,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? 'text.secondary' : LOCKED_INDICATOR_COLOR,
                  letterSpacing: '0.17px',
                }}
              >
                Locked
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {selectedLabel.properties?.description}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ sm: 1 }}
        sx={{ mb: 2, alignItems: 'center' }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <EditIcon sx={{ fontSize: 16 }} color="action" />
          <Typography variant="body2" color="text.secondary">
            Last Modified: {formatDate(selectedLabel.revisionCreateTime)}
          </Typography>
        </Stack>
        <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
          •
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CheckIcon sx={{ fontSize: 16 }} color="action" />
          <Typography variant="body2" color="text.secondary">
            Created Date: {formatDate(selectedLabel.createTime)}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <FieldsCountTile value={fields.length} />
      </Stack>

      <ModelDetailsDisplay title="Basic Information" data={basicInfoData} />

      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', mb: 1, mt: 2, fontSize: 14 }}
      >
        Fields ({fields.length})
      </Typography>
      {fields.length === 0 ? (
        <EmptyStateMessage
          icon={<Filter1Icon fontSize="large" color="disabled" />}
          title="No fields"
          message="This label doesn't have any fields configured."
        />
      ) : (
        <Stack>
          {visibleFields.map((field, index) => {
            const fieldId = field.id ?? `field-${index}`;
            const typeMeta = getFieldTypeMeta(field, isDarkMode);
            const choices = field.selectionOptions?.choices ?? [];
            const isBadgeField = typeMeta.type === 'BADGE_LIST';
            const requiredText = field.properties?.required
              ? 'Required'
              : 'Optional';
            const description = [
              typeMeta.label,
              getFieldAppsText(typeMeta.type, driveEnabled, gmailEnabled),
              requiredText,
            ]
              .filter(Boolean)
              .join(' · ');
            const isFieldDisabled = field.lifecycle?.state === 'DISABLED';

            return (
              <FieldCard
                key={fieldId}
                title={field.properties?.displayName || 'Untitled Field'}
                description={description}
                trailingText={getFieldSummary(field, typeMeta.type)}
                icon={typeMeta.icon}
                iconBg={typeMeta.iconBg}
                expanded={expandedFields.has(fieldId)}
                onToggleExpand={() => toggleField(fieldId)}
                disabled={isFieldDisabled}
                locked={isFieldLocked(field.id)}
              >
                {choices.length > 0 && (
                  <Stack>
                    {choices.map((choice, choiceIndex) => {
                      const badgeColors = getBadgeColorsForChoice(
                        choice,
                        isDarkMode
                      );
                      const isChoiceDisabled =
                        choice.lifecycle?.state === 'DISABLED';
                      const choiceName =
                        choice.properties?.displayName || choice.id;
                      const choiceRow = (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ py: 1, opacity: isChoiceDisabled ? 0.5 : 1 }}
                        >
                          <Typography variant="body2" sx={{ minWidth: 20 }}>
                            {choiceIndex + 1}.
                          </Typography>
                          {/* Badge options keep their colored chip; other lists are plain text. */}
                          {isBadgeField ? (
                            <Chip
                              label={choiceName}
                              size="small"
                              // An option with no color ("No fill") is just its name, like the
                              // field editor and the design: no pill background.
                              sx={
                                badgeColors
                                  ? {
                                      bgcolor: badgeColors.backgroundColor,
                                      color: badgeColors.foregroundColor,
                                    }
                                  : { bgcolor: 'transparent' }
                              }
                            />
                          ) : (
                            <Typography variant="body2">
                              {choiceName}
                            </Typography>
                          )}
                          {isChoiceLocked(field.id, choice.id) && (
                            <Tooltip title="Locked by a Google rule" arrow>
                              <LockOutlinedIcon
                                aria-label="Option locked"
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      );
                      return (
                        <Box key={choice.id ?? choiceIndex}>
                          {isChoiceDisabled ? (
                            <Tooltip
                              title="Disabled"
                              arrow
                              placement="top-start"
                            >
                              {choiceRow}
                            </Tooltip>
                          ) : (
                            choiceRow
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </FieldCard>
            );
          })}
          {fields.length > LIST_VISIBLE_COUNT && (
            <ShowMoreToggle
              expanded={showAllFields}
              onToggle={() => setShowAllFields((prev) => !prev)}
            />
          )}
        </Stack>
      )}

      <Box sx={{ mt: 2 }}>
        <DetailListBox
          title="Permissions"
          items={
            permissionsLoading
              ? []
              : (permissions ?? []).map((permission) => {
                  const { principal, role } = describePermission(permission);
                  return { title: principal, description: role };
                })
          }
          emptyMessage={
            permissionsLoading
              ? 'Loading permissions…'
              : "This label doesn't have any permissions configured."
          }
        />
      </Box>
    </SimpleDrawer>
  );
}
